import { Link } from "@inertiajs/react"
import type { PropsWithChildren } from "react"

import AppLogoIcon from "@/components/app-logo-icon"
import { CrewSwitcher } from "@/components/crew-switcher"
import { HeaderNavUser } from "@/components/header-nav-user"
import { creatorPausedPath } from "@/routes"

/**
 * OKS-404 — Stripped shell for the paused member view.
 *
 * Deliberately NOT the full `CreatorShell`: a paused creator has no app
 * to navigate, so this shell drops the bottom-tab nav, the XP / coins
 * pills, the bell, and the messaging/TikTok banners — everything that
 * implies actionable surfaces. It keeps only:
 *   - the OKS logo (links to `/paused`, a no-op refresh),
 *   - the `CrewSwitcher` (so a multi-brand creator can hop to an active
 *     crew — switching to an active brand restores the full shell,
 *     server-authoritative via the dashboard dispatcher), and
 *   - `HeaderNavUser` (account menu + sign-out).
 *
 * OKS-748 → OKS-799 — OKS-748 carved out ONE exception to the stripping
 * above and rendered `<UnverifiedEmailBanner />` here, directly under the
 * header. OKS-799 removes it: CTPO relocated the prompt off BOTH shells and
 * onto `/profile` + the shared Settings frame. This shell is back to
 * header → content with nothing between them (UX Spec §7).
 *
 * LOAD-BEARING CONSEQUENCE — do not remove or gate the account menu below.
 * `creator_profile_controller.rb` includes `CreatorPaused::RedirectConcern`,
 * so a paused creator hitting `/profile` is 303'd straight back to
 * `/paused`. The `Settings::*` controllers do NOT include it, and
 * `<HeaderNavUser>` → `<UserMenuContent>` is what links to
 * `settingsProfilePath()`. That link is therefore a paused creator's ONLY
 * route to the verification prompt — a one-link dependency, not a
 * redundancy. Locked by spec: drop that menu entry and an unverified paused
 * creator has no way to discover why they'll be blocked on reactivation.
 *
 * Mounted by `Creator/Paused.tsx` in place of `AppLayout`; the global
 * `PersistentLayout` (Toaster, providers) still wraps it.
 */
export function CreatorPausedShell({ children }: PropsWithChildren) {
  return (
    <div className="bg-background flex min-h-screen w-full flex-col">
      <header
        className="border-border/60 bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b px-4 backdrop-blur"
        data-testid="creator-paused-shell-header"
      >
        <Link
          href={creatorPausedPath()}
          aria-label="OKS"
          className="text-sidebar-primary inline-flex h-8 shrink-0 items-center justify-center rounded-md"
        >
          <AppLogoIcon className="h-5 text-current" />
        </Link>
        <div className="flex-1" />
        <CrewSwitcher />
        <HeaderNavUser />
      </header>

      <main className="flex-1" tabIndex={-1}>
        {children}
      </main>
    </div>
  )
}
